// Éditeur du document généré. Le surlignage jaune = marqueur [?...] à résoudre :
// il est VISUEL (mark highlight) mais le gate de validation teste le TEXTE
// (extractMarkers), donc retirer le surlignage sans corriger le texte ne débloque rien.
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import { Bold, Italic, List, ListOrdered, Heading2 } from 'lucide-react';
import { cn } from '../../lib/cn';
import type { TiptapNode } from '../../services/documents/markdownDoc';

interface Props {
  initialDoc: TiptapNode;
  onUpdate: (doc: TiptapNode, text: string) => void;
  className?: string;
}

export function DocumentEditor({ initialDoc, onUpdate, className }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Highlight,
    ],
    content: initialDoc as never,
    onUpdate: ({ editor: e }) => onUpdate(e.getJSON() as TiptapNode, e.getText()),
  });

  if (!editor) return null;

  const btn = (active: boolean) =>
    cn(
      'rounded p-1.5 cursor-pointer',
      active ? 'bg-cime-primary/10 text-cime-primary' : 'text-neutral-500 hover:text-neutral-800',
    );

  return (
    <div className={cn('overflow-hidden rounded border border-neutral-200 bg-white', className)}>
      <div className="flex items-center gap-1 border-b border-neutral-200 px-2 py-1">
        <button type="button" title="Gras" className={btn(editor.isActive('bold'))}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="w-4 h-4" aria-hidden />
        </button>
        <button type="button" title="Italique" className={btn(editor.isActive('italic'))}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="w-4 h-4" aria-hidden />
        </button>
        <button type="button" title="Titre" className={btn(editor.isActive('heading', { level: 2 }))}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="w-4 h-4" aria-hidden />
        </button>
        <button type="button" title="Liste à puces" className={btn(editor.isActive('bulletList'))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="w-4 h-4" aria-hidden />
        </button>
        <button type="button" title="Liste numérotée" className={btn(editor.isActive('orderedList'))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="w-4 h-4" aria-hidden />
        </button>
      </div>
      <EditorContent
        editor={editor}
        className={cn(
          'px-4 py-3 min-h-[320px] text-sm text-neutral-800 [&_.ProseMirror]:outline-none',
          '[&_mark]:bg-amber-200 [&_mark]:rounded [&_mark]:px-0.5',
          '[&_h1]:text-xl [&_h1]:font-semibold [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:font-semibold',
          '[&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
        )}
      />
    </div>
  );
}
